import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';

import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useNavigate, useSearchParams } from 'react-router';
import { ErrorBoundary as ErrorBoundaryComponent } from '~/components/molecules/error-boundary/error-boundary';
import { RouteLoadingBar } from '~/components/molecules/route-loading-bar/route-loading-bar';
import { Toaster } from '~/components/ui/sonner';
import type { Route } from './+types/root';
import stylesheet from './app.css?url';
import { AuthLoader } from './components/molecules/auth-loader/auth-loader';
import { Auth0ErrorSection } from './components/molecules/auth0-error-section/auth0-error-section';
import { CommandBar } from './components/organisms/command-bar';
import { KeyboardShortcutsDialog } from './components/organisms/keyboard-shortcuts-dialog';
import { MeetingDialog } from './components/organisms/meeting-dialog';
import { ScheduledTaskDialogHost } from './components/organisms/scheduled-task-dialog-host';
import i18n, { defaultNS, i18nReady } from './i18n';
import { AnalyticsProvider } from './lib/analytics';
import { sentryInit } from './lib/error-tracking';
import { FeatureFlagProvider } from './lib/feature-flags';
import { ThemeProvider } from './lib/theme-provider/theme-provider';
import { preloadCriticalAssets } from './lib/utils/preloader';
import { createQueryClient } from './utils/create-query-client';

/**
 * React Query devtools are dev-only — both to keep the production bundle lean
 * and because the panel is noisy for non-developers.
 */
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((mod) => ({
        default: mod.ReactQueryDevtools,
      })),
    )
  : null;

export const links: Route.LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Geist:wght@100..900&display=swap',
  },
  {
    rel: 'stylesheet',
    href: 'https://cdn.jsdelivr.net/npm/@vercel/fonts@latest/geist-mono/stylesheet.css',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Sora:wght@400;600&display=swap',
  },
  { rel: 'stylesheet', href: stylesheet },
];

const themeInitScript = `(function(){try{var t=localStorage.getItem('systemzero-theme')||'system';var c=t==='system'?window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light':t;document.documentElement.classList.add(c)}catch(e){}})()`;

export function Layout({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <title>Systemzero app</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        {/* ChatKit web component script */}
        <script src="https://cdn.platform.openai.com/deployments/chatkit/chatkit.js" async />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Initialize Sentry if DSN is provided
if (import.meta.env.VITE_SENTRY_DSN) sentryInit();

preloadCriticalAssets().catch(console.error);

export default function App() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready } = useTranslation();
  const [hydrated, setHydrated] = useState(false);
  const [queryClient] = useState(createQueryClient);
  const [i18nLoaded, setI18nLoaded] = useState(() => i18n.isInitialized && i18n.hasLoadedNamespace(defaultNS));

  useEffect(() => {
    setHydrated(true);
    console.log(import.meta.env.VITE_VERSION);
  }, []);

  // `ready` from useTranslation can miss the load and stay false, leaving the
  // skeleton up forever on reload. Track readiness directly as a fallback.
  useEffect(() => {
    if (i18nLoaded) return;
    const markLoaded = () => setI18nLoaded(true);
    if (i18n.isInitialized && i18n.hasLoadedNamespace(defaultNS)) markLoaded();
    i18nReady.then(markLoaded, markLoaded);
    i18n.on('loaded', markLoaded);
    return () => i18n.off('loaded', markLoaded);
  }, [i18nLoaded]);

  const authConfig = {
    domain: import.meta.env.VITE_AUTH_DOMAIN || '',
    clientId: import.meta.env.VITE_AUTH_CLIENT_ID || '',
    audience: import.meta.env.VITE_AUTH_AUDIENCE || '',
  };

  const onRedirectCallback = (appState: { returnTo?: string } | undefined) => {
    let target = appState?.returnTo;
    if (!target || target === '/') {
      try {
        target = sessionStorage.getItem('sz:auth_return_to') ?? undefined;
        sessionStorage.removeItem('sz:auth_return_to');
      } catch {
        // sessionStorage unavailable
      }
    }
    navigate(target ?? globalThis.location?.pathname ?? '/');
  };

  // Auth0 has to mount before translations finish, otherwise the code exchange
  // waits on /locales/en/translation.json. The skeleton still covers the UI
  // until `ready`, so raw keys do not flash. See SZ-3370.
  if (!hydrated) {
    return <AuthLoader />;
  }

  const translationsReady = ready || i18nLoaded;

  return (
    <>
      <RouteLoadingBar />
      <Auth0Provider
        domain={authConfig.domain}
        clientId={authConfig.clientId}
        authorizationParams={{
          redirect_uri: `${window.location.origin}`,
          audience: authConfig.audience,
          scope: 'openid profile email offline_access',
        }}
        useRefreshTokens={true}
        useRefreshTokensFallback={true}
        cacheLocation="localstorage"
        onRedirectCallback={onRedirectCallback}
      >
        <QueryClientProvider client={queryClient}>
          <AnalyticsProvider>
            <FeatureFlagProvider>
              <ThemeProvider storageKey="systemzero-theme">
                {!translationsReady ? <AuthLoader /> : searchParams.get('error') ? <Auth0ErrorSection /> : <Outlet />}
                {translationsReady ? (
                  <>
                    <CommandBar />
                    <ScheduledTaskDialogHost />
                    <MeetingDialog />
                    <KeyboardShortcutsDialog />
                  </>
                ) : null}
              </ThemeProvider>
            </FeatureFlagProvider>
          </AnalyticsProvider>
          {ReactQueryDevtools ? (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          ) : null}
        </QueryClientProvider>
        <Toaster closeButton={true} visibleToasts={1} />
      </Auth0Provider>
    </>
  );
}

/**
 * Resolves the active theme name from the stored preference.
 *
 * When the stored value is "system", it reads the OS colour-scheme preference
 * via `matchMedia`. Falls back to "light" in environments where `matchMedia`
 * is unavailable (SSR, test, old browsers).
 *
 * @param stored - Value from localStorage (e.g. "light", "dark", or "system")
 * @returns The resolved theme: "light" or "dark", or the stored value verbatim
 */
function resolveTheme(stored: string): string {
  if (stored !== 'system') {
    return stored;
  }
  const prefersDark = globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  return prefersDark ? 'dark' : 'light';
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  console.log('error', error);

  useEffect(() => {
    const root = document.documentElement;
    if (!root.classList.contains('dark') && !root.classList.contains('light')) {
      const stored = localStorage.getItem('systemzero-theme') || 'system';
      const resolved = resolveTheme(stored);
      root.classList.add(resolved);
    }
  }, []);

  return <ErrorBoundaryComponent fallbackUrls={{ default: '/' }} error={error} />;
}

export function HydrateFallback() {
  return <AuthLoader mode="full" />;
}
